import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { usePersistedState, usePersistedRef } from '../hooks/usePersistedState';
import {
  intelligenceGraphSnapshot,
  intelligenceGraphBlastRadius,
  intelligenceGraphDependencies,
  intelligenceGraphNode,
  intelligenceGraphSearch,
} from '../services/api';

// ─── Constants ───────────────────────────────────────────────────
const BRAND = { primary: '#0d9488', dark: '#0a766b', light: '#e3f4f1' };
const STONE = {
  50: '#fafaf9', 100: '#f5f5f4', 200: '#e7e5e4', 300: '#d6d3d1',
  400: '#a8a29e', 500: '#78716c', 600: '#57534e', 700: '#44403c', 900: '#1c1917'
};

const HEALTH_COLORS = {
  HEALTHY: '#10B981', DEGRADED: '#D97706', IMPAIRED: '#EA580C',
  CRITICAL: '#DC2626', UNKNOWN: '#6B7280'
};

const NODE_COLORS = {
  ORGANIZATION: '#7C3AED', TENANT: '#2563EB', AGENT: '#059669',
  SKILL: '#0891B2', WORKFLOW: '#D97706', WORKFLOW_NODE: '#EA580C',
  CONNECTOR: '#E11D48', CREDENTIAL: '#6366F1', POLICY: '#8B5CF6',
  APPROVAL: '#F59E0B', EXECUTION: '#14B8A6', EVENT: '#EF4444'
};

const NODE_SHAPES = {
  ORGANIZATION: 'nebula', TENANT: 'sphere', AGENT: 'polyhedron',
  SKILL: 'diamond', WORKFLOW: 'torus', CONNECTOR: 'cylinder',
  CREDENTIAL: 'key', POLICY: 'shield', EVENT: 'bolt'
};

// Node type hierarchy for drill-down
const TYPE_HIERARCHY = ['ORGANIZATION', 'TENANT', 'AGENT', 'WORKFLOW', 'SKILL', 'CONNECTOR', 'CREDENTIAL', 'POLICY', 'EVENT'];

// ─── Force-directed 3D layout ────────────────────────────────────
function forceLayout3D(nodes, edges) {
  const positions = {};
  const LAYER_SPACING = 120;
  const typeGroups = {};

  nodes.forEach(n => {
    if (!typeGroups[n.type]) typeGroups[n.type] = [];
    typeGroups[n.type].push(n);
  });

  const depthOrder = TYPE_HIERARCHY;

  nodes.forEach(n => {
    const depthIdx = depthOrder.indexOf(n.type);
    const depth = depthIdx >= 0 ? depthIdx : n.depth || 0;
    const group = typeGroups[n.type] || [];
    const idx = group.indexOf(n);
    const count = group.length;

    const angle = (idx / Math.max(count, 1)) * Math.PI * 2;
    const radius = 60 + count * 8;

    positions[n.id] = {
      x: Math.cos(angle) * radius,
      y: -depth * LAYER_SPACING,
      z: Math.sin(angle) * radius,
    };
  });

  // Simple force simulation (5 iterations)
  for (let iter = 0; iter < 5; iter++) {
    nodes.forEach(a => {
      nodes.forEach(b => {
        if (a.id === b.id) return;
        const pa = positions[a.id];
        const pb = positions[b.id];
        const dx = pa.x - pb.x;
        const dy = pa.y - pb.y;
        const dz = pa.z - pb.z;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
        if (dist < 120) {
          const force = (120 - dist) * 0.05;
          pa.x += (dx / dist) * force;
          pa.z += (dz / dist) * force;
        }
      });
    });

    edges.forEach(e => {
      const ps = positions[e.source];
      const pt = positions[e.target];
      if (!ps || !pt) return;
      const dx = pt.x - ps.x;
      const dz = pt.z - ps.z;
      const dist = Math.sqrt(dx * dx + dz * dz) || 1;
      if (dist > 100) {
        const force = (dist - 100) * 0.01;
        ps.x += (dx / dist) * force;
        ps.z += (dz / dist) * force;
        pt.x -= (dx / dist) * force;
        pt.z -= (dz / dist) * force;
      }
    });
  }

  return positions;
}

// ─── Canvas 3D Renderer (no Three.js dependency) ─────────────────
function project3D(x, y, z, camera) {
  const cosY = Math.cos(camera.rotY);
  const sinY = Math.sin(camera.rotY);
  const cosX = Math.cos(camera.rotX);
  const sinX = Math.sin(camera.rotX);

  let tx = x - camera.x;
  let ty = y - camera.y;
  let tz = z - camera.z;

  let rx = tx * cosY + tz * sinY;
  let rz = -tx * sinY + tz * cosY;

  let ry = ty * cosX - rz * sinX;
  let rz2 = ty * sinX + rz * cosX;

  const fov = 600 / camera.zoom;
  const scale = fov / (fov + rz2);

  return { sx: rx * scale, sy: ry * scale, scale, depth: rz2 };
}

// ─── Main Component ──────────────────────────────────────────────
export default function KnowledgeGraph3D({ onNodeSelect, embedded = false }) {
  const canvasRef = useRef(null);
  const [graphData, setGraphData] = useState({ nodes: [], edges: [] });
  const [positions, setPositions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [blastRadiusNodes, setBlastRadiusNodes] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [nodeDetail, setNodeDetail] = useState(null);
  const [stats, setStats] = useState(null);

  // Persisted state — survives navigation away and back
  const [darkMode, setDarkMode] = usePersistedState('graph3d.darkMode', true);
  const [focusedNode, setFocusedNode] = useState(null); // node we've zoomed into
  const [breadcrumbs, setBreadcrumbs] = useState([{ label: 'Platform', nodeId: null, type: null }]);
  const [visibleTypes, setVisibleTypes] = useState(null); // null = show all
  const [showLegend, setShowLegend] = usePersistedState('graph3d.showLegend', !embedded);
  const [showControls, setShowControls] = usePersistedState('graph3d.showControls', !embedded);
  const [zoomPercent, setZoomPercent] = useState(100);

  // Theme colors derived from mode
  const BG = darkMode ? STONE[900] : '#ffffff';
  const BG_GRID = darkMode ? (STONE[700] + '30') : (STONE[300] + '50');
  const TEXT_PRIMARY = darkMode ? STONE[100] : STONE[900];
  const TEXT_SECONDARY = darkMode ? STONE[300] : STONE[600];
  const TEXT_MUTED = darkMode ? STONE[400] : STONE[500];
  const PANEL_BG = darkMode ? (STONE[700] + 'F0') : ('rgba(255,255,255,0.95)');
  const PANEL_BORDER = darkMode ? STONE[600] : STONE[200];
  const BUTTON_BG = darkMode ? (STONE[700] + '80') : ('rgba(255,255,255,0.9)');
  const BUTTON_TEXT = darkMode ? STONE[200] : STONE[700];
  const HOVER_BG = darkMode ? STONE[600] : STONE[200];

  // Default: zoomed-out cosmos view showing entire graph
  const COSMOS_CAM = { x: 0, y: -350, z: 900, rotX: 0.55, rotY: 0.15, zoom: 1 };
  const cameraRef = usePersistedRef('graph3d.camera.v2', {
    ...COSMOS_CAM,
    targetX: COSMOS_CAM.x, targetY: COSMOS_CAM.y, targetZ: COSMOS_CAM.z,
    targetRotX: COSMOS_CAM.rotX, targetRotY: COSMOS_CAM.rotY, targetZoom: COSMOS_CAM.zoom,
  }, { saveIntervalMs: 1000 });
  const isDragging = useRef(false);
  const isPanning = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  const animationRef = useRef(null);
  const hasAutoCentered = useRef(false);

  // ─── Load Graph Data ────────────────────────────────────────
  useEffect(() => { loadGraph(); }, []);

  const loadGraph = async () => {
    setLoading(true);
    try {
      const res = await intelligenceGraphSnapshot();
      if (res.ok && res.data) {
        setGraphData(res.data);
        const pos = forceLayout3D(res.data.nodes || [], res.data.edges || []);
        setPositions(pos);
        setStats({ nodeCount: res.data.nodeCount, edgeCount: res.data.edgeCount });
      }
    } catch (e) {
      setError('Failed to load Knowledge Graph');
    }
    setLoading(false);
  };

  // Auto-center graph on first data load
  useEffect(() => {
    if (hasAutoCentered.current) return;
    const ids = Object.keys(positions);
    if (ids.length === 0 || graphData.nodes.length === 0) return;
    hasAutoCentered.current = true;

    // Compute centroid and fit camera
    let cx = 0, cy = 0, cz = 0, count = 0;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity, minZ = Infinity, maxZ = -Infinity;
    graphData.nodes.forEach(n => {
      const p = positions[n.id];
      if (!p) return;
      cx += p.x; cy += p.y; cz += p.z;
      minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
      minZ = Math.min(minZ, p.z); maxZ = Math.max(maxZ, p.z);
      count++;
    });
    if (count === 0) return;
    cx /= count; cy /= count; cz /= count;
    const span = Math.max(maxX - minX, maxY - minY, maxZ - minZ, 200);

    const cam = cameraRef.current;
    cam.targetX = cx; cam.targetY = cy - span * 0.35; cam.targetZ = cz + span * 1.2;
    cam.targetRotX = 0.45; cam.targetRotY = 0; cam.targetZoom = 1;
    // Also set immediate position for no-delay on first paint
    cam.x = cam.targetX; cam.y = cam.targetY; cam.z = cam.targetZ;
    cam.rotX = cam.targetRotX; cam.rotY = cam.targetRotY; cam.zoom = cam.targetZoom;
  }, [positions, graphData.nodes]); // eslint-disable-line

  // ─── Filtered nodes/edges based on drill-down focus ─────────
  const filteredData = useMemo(() => {
    const nodes = graphData.nodes || [];
    const edges = graphData.edges || [];

    if (!focusedNode) {
      // If visibleTypes filter is set, filter by type
      if (visibleTypes) {
        const filteredNodes = nodes.filter(n => visibleTypes.has(n.type));
        const nodeIds = new Set(filteredNodes.map(n => n.id));
        const filteredEdges = edges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target));
        return { nodes: filteredNodes, edges: filteredEdges };
      }
      return { nodes, edges };
    }

    // Drill-down: show focused node + its direct connections + their children
    const focusId = focusedNode.id;
    const connectedIds = new Set([focusId]);

    // Gather direct connections (1 hop)
    edges.forEach(e => {
      if (e.source === focusId) connectedIds.add(e.target);
      if (e.target === focusId) connectedIds.add(e.source);
    });

    // Gather 2nd-hop children (2 hops from focus)
    edges.forEach(e => {
      if (connectedIds.has(e.source)) connectedIds.add(e.target);
      if (connectedIds.has(e.target)) connectedIds.add(e.source);
    });

    const filteredNodes = nodes.filter(n => connectedIds.has(n.id));
    const nodeIds = new Set(filteredNodes.map(n => n.id));
    const filteredEdges = edges.filter(e => nodeIds.has(e.source) && nodeIds.has(e.target));

    return { nodes: filteredNodes, edges: filteredEdges };
  }, [graphData, focusedNode, visibleTypes]);

  // Recompute positions when filtered data changes
  const activePositions = useMemo(() => {
    if (filteredData.nodes.length === 0) return positions;
    if (!focusedNode) return positions;
    // Re-layout the filtered subgraph
    return forceLayout3D(filteredData.nodes, filteredData.edges);
  }, [filteredData, focusedNode, positions]);

  // ─── Compute node importance for dynamic sizing ─────────────
  const nodeImportance = useMemo(() => {
    const importance = {};
    const renderNodes = filteredData.nodes;
    const renderEdges = filteredData.edges;

    // Count connections per node
    const connectionCount = {};
    renderNodes.forEach(n => { connectionCount[n.id] = 0; });
    renderEdges.forEach(e => {
      if (connectionCount[e.source] != null) connectionCount[e.source]++;
      if (connectionCount[e.target] != null) connectionCount[e.target]++;
    });

    // Find maxes for normalization
    let maxConnections = 1;
    let maxBlast = 1;
    renderNodes.forEach(n => {
      maxConnections = Math.max(maxConnections, connectionCount[n.id] || 0);
      maxBlast = Math.max(maxBlast, n.blastRadius || 0);
    });

    // Compute importance: blend of connections, blast radius, and type hierarchy
    renderNodes.forEach(n => {
      const connNorm = (connectionCount[n.id] || 0) / maxConnections; // 0..1
      const blastNorm = (n.blastRadius || 0) / maxBlast;             // 0..1
      const typeIdx = TYPE_HIERARCHY.indexOf(n.type);
      // Higher hierarchy = bigger; ORG=0 → 1.0, TENANT=1 → 0.88, etc.
      const typeWeight = typeIdx >= 0 ? 1.0 - (typeIdx / TYPE_HIERARCHY.length) * 0.7 : 0.3;

      // Weighted blend: 30% connections, 35% blast radius, 35% hierarchy
      importance[n.id] = connNorm * 0.30 + blastNorm * 0.35 + typeWeight * 0.35;
    });

    return importance;
  }, [filteredData]);

  // ─── Disambiguated labels for nodes with duplicate names ────
  const nodeLabels = useMemo(() => {
    const labels = {};
    const renderNodes = filteredData.nodes;
    // Count how many times each name appears per type
    const nameCount = {};
    renderNodes.forEach(n => {
      const key = `${n.type}::${n.name}`;
      nameCount[key] = (nameCount[key] || 0) + 1;
    });
    // For duplicates, append parent org/tenant name
    renderNodes.forEach(n => {
      const key = `${n.type}::${n.name}`;
      if (nameCount[key] > 1 && n.parentId) {
        const parent = renderNodes.find(p => p.id === n.parentId);
        if (parent) {
          labels[n.id] = `${n.name} (${parent.name})`;
        } else {
          labels[n.id] = n.name;
        }
      } else {
        labels[n.id] = n.name;
      }
    });
    return labels;
  }, [filteredData]);

  // ─── Canvas Rendering ───────────────────────────────────────
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    const cam = cameraRef.current;

    // Smooth camera animation
    cam.x += (cam.targetX - cam.x) * 0.08;
    cam.y += (cam.targetY - cam.y) * 0.08;
    cam.z += (cam.targetZ - cam.z) * 0.08;
    cam.rotX += (cam.targetRotX - cam.rotX) * 0.08;
    cam.rotY += (cam.targetRotY - cam.rotY) * 0.08;
    cam.zoom += (cam.targetZoom - cam.zoom) * 0.08;

    // Update zoom percentage display
    const newPercent = Math.round(cam.zoom * 100);
    if (Math.abs(newPercent - zoomPercent) > 1) {
      // We can't setState in render loop, so we'll use a ref-based approach
    }

    // Clear with current background
    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, w, h);

    // Draw grid
    ctx.strokeStyle = BG_GRID;
    ctx.lineWidth = 0.5;
    for (let i = -500; i <= 500; i += 50) {
      const p1 = project3D(i, 0, -500, cam);
      const p2 = project3D(i, 0, 500, cam);
      if (p1.depth > 0 && p2.depth > 0) {
        ctx.beginPath();
        ctx.moveTo(w / 2 + p1.sx, h / 2 + p1.sy);
        ctx.lineTo(w / 2 + p2.sx, h / 2 + p2.sy);
        ctx.stroke();
      }
    }

    const renderNodes = filteredData.nodes;
    const renderEdges = filteredData.edges;
    const posMap = focusedNode ? activePositions : positions;

    // Project all nodes
    const projected = [];
    renderNodes.forEach(node => {
      const pos = posMap[node.id];
      if (!pos) return;
      const p = project3D(pos.x, pos.y, pos.z, cam);
      if (p.depth > -100) {
        projected.push({ ...node, sx: w / 2 + p.sx, sy: h / 2 + p.sy, scale: p.scale, depth: p.depth });
      }
    });

    projected.sort((a, b) => b.depth - a.depth);

    // Draw edges
    ctx.lineWidth = 1;
    renderEdges.forEach(edge => {
      const sp = posMap[edge.source];
      const tp = posMap[edge.target];
      if (!sp || !tp) return;

      const s = project3D(sp.x, sp.y, sp.z, cam);
      const t = project3D(tp.x, tp.y, tp.z, cam);
      if (s.depth < -100 || t.depth < -100) return;

      const isBlastEdge = blastRadiusNodes.has(edge.source) || blastRadiusNodes.has(edge.target);

      ctx.strokeStyle = isBlastEdge ? '#EF444480' :
        edge.type === 'CASCADES_TO' ? '#EF444440' :
        edge.type === 'USES_CONNECTOR' ? '#0891B240' :
        edge.type === 'HAS_SKILL' ? '#05966940' :
        (darkMode ? STONE[500] + '30' : STONE[400] + '40');
      ctx.lineWidth = isBlastEdge ? 2 : 1;

      ctx.beginPath();
      ctx.moveTo(w / 2 + s.sx, h / 2 + s.sy);
      ctx.lineTo(w / 2 + t.sx, h / 2 + t.sy);
      ctx.stroke();

      // Draw arrowhead for directed edges
      if (ctx.lineWidth >= 1 && s.scale > 0.3) {
        const angle = Math.atan2(t.sy - s.sy, t.sx - s.sx);
        const arrLen = 6 * Math.min(s.scale, t.scale);
        const mx = w / 2 + (s.sx + t.sx) / 2;
        const my = h / 2 + (s.sy + t.sy) / 2;
        ctx.beginPath();
        ctx.moveTo(mx, my);
        ctx.lineTo(mx - arrLen * Math.cos(angle - 0.4), my - arrLen * Math.sin(angle - 0.4));
        ctx.moveTo(mx, my);
        ctx.lineTo(mx - arrLen * Math.cos(angle + 0.4), my - arrLen * Math.sin(angle + 0.4));
        ctx.stroke();
      }
    });

    // Draw nodes
    projected.forEach(node => {
      // Dynamic sizing based on importance (blast radius + connections + hierarchy)
      const imp = nodeImportance[node.id] || 0.3;
      // Scale range: importance 0→min size, importance 1→max size
      const minR = 5, maxR = 28;
      const baseSize = Math.max(4, (minR + imp * (maxR - minR)) * node.scale);
      const isHovered = hoveredNode === node.id;
      const isSelected = selectedNode === node.id;
      const isFocused = focusedNode && focusedNode.id === node.id;
      const isBlast = blastRadiusNodes.has(node.id);
      const size = isHovered ? baseSize * 1.4 : (isSelected || isFocused) ? baseSize * 1.3 : baseSize;

      const healthColor = HEALTH_COLORS[node.healthBand] || HEALTH_COLORS.UNKNOWN;
      const nodeColor = NODE_COLORS[node.type] || '#6B7280';

      // Glow effect
      if (isSelected || isBlast || isFocused) {
        const grad = ctx.createRadialGradient(node.sx, node.sy, 0, node.sx, node.sy, size * 3);
        grad.addColorStop(0, (isBlast ? '#EF4444' : isFocused ? '#7C3AED' : BRAND.primary) + '40');
        grad.addColorStop(1, 'transparent');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(node.sx, node.sy, size * 3, 0, Math.PI * 2);
        ctx.fill();
      }

      // Health ring
      ctx.beginPath();
      ctx.arc(node.sx, node.sy, size + 2, 0, Math.PI * 2);
      ctx.fillStyle = healthColor + '60';
      ctx.fill();

      // Node body
      ctx.beginPath();
      ctx.arc(node.sx, node.sy, size, 0, Math.PI * 2);
      ctx.fillStyle = nodeColor;
      ctx.fill();

      // Inner highlight
      if (size > 6) {
        ctx.beginPath();
        ctx.arc(node.sx - size * 0.2, node.sy - size * 0.2, size * 0.4, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff30';
        ctx.fill();
      }

      // Drill-down indicator (small + icon for expandable nodes)
      if (size > 8 && hasChildren(node)) {
        ctx.font = `bold ${Math.max(8, 10 * node.scale)}px Arial`;
        ctx.fillStyle = darkMode ? '#ffffff80' : '#00000060';
        ctx.textAlign = 'center';
        ctx.fillText('+', node.sx + size + 4, node.sy - size + 4);
      }

      // Label (use disambiguated labels for duplicate names)
      const displayLabel = nodeLabels[node.id] || node.name;
      if (size > 8 && displayLabel) {
        ctx.font = `${Math.max(9, 11 * node.scale)}px Arial`;
        ctx.fillStyle = TEXT_PRIMARY;
        ctx.textAlign = 'center';

        // Drop shadow for readability
        if (!darkMode) {
          ctx.save();
          ctx.shadowColor = 'rgba(255,255,255,0.8)';
          ctx.shadowBlur = 4;
          ctx.fillText(displayLabel, node.sx, node.sy + size + 14);
          ctx.restore();
        } else {
          ctx.fillText(displayLabel, node.sx, node.sy + size + 14);
        }

        // Type badge
        ctx.font = `${Math.max(7, 9 * node.scale)}px Arial`;
        ctx.fillStyle = nodeColor + 'AA';
        ctx.fillText(node.type, node.sx, node.sy + size + 24);
      }

      // Health percentage for hovered
      if (isHovered && node.health != null) {
        ctx.font = `bold ${Math.max(10, 12 * node.scale)}px Arial`;
        ctx.fillStyle = healthColor;
        ctx.textAlign = 'center';
        ctx.fillText(`${Math.round(node.health)}%`, node.sx, node.sy - size - 8);
      }
    });

    // Zoom indicator in bottom-right of canvas
    ctx.font = '11px Arial';
    ctx.fillStyle = TEXT_MUTED;
    ctx.textAlign = 'right';
    ctx.fillText(`${Math.round(cam.zoom * 100)}%`, w - 16, h - 16);

    animationRef.current = requestAnimationFrame(render);
  }, [filteredData, positions, activePositions, hoveredNode, selectedNode, focusedNode,
      blastRadiusNodes, darkMode, BG, BG_GRID, TEXT_PRIMARY, TEXT_MUTED, zoomPercent, nodeImportance, nodeLabels]);

  // Check if a node type has potential children in the hierarchy
  function hasChildren(node) {
    const idx = TYPE_HIERARCHY.indexOf(node.type);
    if (idx < 0 || idx >= TYPE_HIERARCHY.length - 1) return false;
    // Check if any edges go from this node to children
    return (graphData.edges || []).some(e => e.source === node.id || e.target === node.id);
  }

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resizeCanvas = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    animationRef.current = requestAnimationFrame(render);
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [render]);

  // ─── Keyboard shortcuts ────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT') return;

      switch (e.key) {
        case '+':
        case '=':
          zoomIn();
          break;
        case '-':
        case '_':
          zoomOut();
          break;
        case '0':
          resetCamera();
          break;
        case 'Escape':
          if (focusedNode) drillUp();
          else { setSelectedNode(null); setBlastRadiusNodes(new Set()); setNodeDetail(null); }
          break;
        case 'f':
        case 'F':
          if (!e.ctrlKey && !e.metaKey) setShowSearch(s => !s);
          break;
        case 'l':
        case 'L':
          setShowLegend(s => !s);
          break;
        case 'b':
        case 'B':
          setDarkMode(m => !m);
          break;
        case 'c':
        case 'C':
          if (!e.ctrlKey && !e.metaKey) setShowControls(s => !s);
          break;
        case 'g':
        case 'G':
          centerGraph();
          break;
        case 'ArrowUp':
          e.preventDefault();
          panCamera(0, 30, 0);
          break;
        case 'ArrowDown':
          e.preventDefault();
          panCamera(0, -30, 0);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          panCamera(30, 0, 0);
          break;
        case 'ArrowRight':
          e.preventDefault();
          panCamera(-30, 0, 0);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusedNode]);

  // ─── Camera Controls ────────────────────────────────────────
  const zoomIn = () => {
    const cam = cameraRef.current;
    cam.targetZ = Math.max(50, cam.targetZ - 80);
    cam.targetZoom = Math.min(5, cam.targetZoom * 1.2);
    setZoomPercent(Math.round(cam.targetZoom * 100));
  };

  const zoomOut = () => {
    const cam = cameraRef.current;
    cam.targetZ = Math.min(2000, cam.targetZ + 80);
    cam.targetZoom = Math.max(0.2, cam.targetZoom / 1.2);
    setZoomPercent(Math.round(cam.targetZoom * 100));
  };

  const panCamera = (dx, dy, dz) => {
    const cam = cameraRef.current;
    cam.targetX += dx;
    cam.targetY += dy;
    cam.targetZ += dz;
  };

  const resetCamera = () => {
    const cam = cameraRef.current;
    cam.targetX = COSMOS_CAM.x;
    cam.targetY = COSMOS_CAM.y;
    cam.targetZ = COSMOS_CAM.z;
    cam.targetRotX = COSMOS_CAM.rotX;
    cam.targetRotY = COSMOS_CAM.rotY;
    cam.targetZoom = COSMOS_CAM.zoom;
    setBlastRadiusNodes(new Set());
    setSelectedNode(null);
    setFocusedNode(null);
    setVisibleTypes(null);
    setBreadcrumbs([{ label: 'Platform', nodeId: null, type: null }]);
    setNodeDetail(null);
    setZoomPercent(100);
  };

  // ─── Center / Fit graph to viewport ─────────────────────────
  const centerGraph = () => {
    const ids = Object.keys(positions);
    if (ids.length === 0) return;

    // Compute centroid of all visible nodes
    let cx = 0, cy = 0, cz = 0, count = 0;
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;

    const visibleNodes = graphData.nodes.filter(
      n => !visibleTypes || visibleTypes.has(n.type)
    );

    visibleNodes.forEach(n => {
      const p = positions[n.id];
      if (!p) return;
      cx += p.x; cy += p.y; cz += p.z;
      minX = Math.min(minX, p.x); maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y); maxY = Math.max(maxY, p.y);
      minZ = Math.min(minZ, p.z); maxZ = Math.max(maxZ, p.z);
      count++;
    });

    if (count === 0) return;
    cx /= count; cy /= count; cz /= count;

    // Compute bounding sphere radius
    const spanX = maxX - minX;
    const spanY = maxY - minY;
    const spanZ = maxZ - minZ;
    const span = Math.max(spanX, spanY, spanZ, 200);

    // Position camera above-behind the centroid, far enough to see all nodes
    const cam = cameraRef.current;
    cam.targetX = cx;
    cam.targetY = cy - span * 0.35;
    cam.targetZ = cz + span * 1.2;
    cam.targetRotX = 0.45;
    cam.targetRotY = 0;
    cam.targetZoom = 1;
    setZoomPercent(100);
  };

  // ─── Drill-down into a node (zoom to see its structure) ─────
  const drillInto = async (node) => {
    // Avoid drilling into the same node or leaf-type nodes
    if (focusedNode && focusedNode.id === node.id) return;

    setFocusedNode(node);
    setSelectedNode(node.id);
    setNodeDetail(null);

    // Update breadcrumbs (skip if already present)
    setBreadcrumbs(prev => {
      if (prev.some(b => b.nodeId === node.id)) return prev;
      return [...prev, { label: node.name || node.type, nodeId: node.id, type: node.type }];
    });

    // Animate camera to the focused node — use current positions map
    const pos = positions[node.id];
    if (pos) {
      const cam = cameraRef.current;
      cam.targetX = pos.x;
      cam.targetY = pos.y;
      cam.targetZ = pos.z + 150;
      cam.targetZoom = Math.min(8, cam.targetZoom * 1.8);
      setZoomPercent(Math.round(cam.targetZoom * 100));
    }

    // Load detail for the side panel
    const res = await intelligenceGraphNode(node.id);
    if (res.ok) setNodeDetail(res.data);

    if (onNodeSelect) onNodeSelect(node);
  };

  const drillUp = () => {
    if (breadcrumbs.length <= 1) {
      resetCamera();
      return;
    }

    const newBreadcrumbs = breadcrumbs.slice(0, -1);
    setBreadcrumbs(newBreadcrumbs);

    const parentCrumb = newBreadcrumbs[newBreadcrumbs.length - 1];
    if (parentCrumb.nodeId) {
      const parentNode = (graphData.nodes || []).find(n => n.id === parentCrumb.nodeId);
      if (parentNode) {
        setFocusedNode(parentNode);
        const pos = positions[parentNode.id];
        if (pos) {
          const cam = cameraRef.current;
          cam.targetX = pos.x;
          cam.targetY = pos.y;
          cam.targetZ = pos.z + 200;
          cam.targetZoom = Math.max(0.2, cam.targetZoom / 1.5);
          setZoomPercent(Math.round(cam.targetZoom * 100));
        }
      }
    } else {
      // Back to root
      setFocusedNode(null);
      const cam = cameraRef.current;
      cam.targetX = 0;
      cam.targetY = -200;
      cam.targetZ = 500;
      cam.targetZoom = 1;
      setZoomPercent(100);
    }
  };

  const drillToBreadcrumb = (index) => {
    const crumb = breadcrumbs[index];
    const newBreadcrumbs = breadcrumbs.slice(0, index + 1);
    setBreadcrumbs(newBreadcrumbs);

    if (crumb.nodeId) {
      const node = (graphData.nodes || []).find(n => n.id === crumb.nodeId);
      if (node) {
        setFocusedNode(node);
        const pos = positions[node.id];
        if (pos) {
          cameraRef.current.targetX = pos.x;
          cameraRef.current.targetY = pos.y;
          cameraRef.current.targetZ = pos.z + 200;
        }
      }
    } else {
      resetCamera();
    }
  };

  // ─── Mouse Interaction ──────────────────────────────────────
  const getNodeAtPosition = useCallback((mx, my) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const w = canvas.width;
    const h = canvas.height;
    const cam = cameraRef.current;
    const posMap = focusedNode ? activePositions : positions;

    let closest = null;
    let closestDist = Infinity;

    filteredData.nodes.forEach(node => {
      const pos = posMap[node.id];
      if (!pos) return;
      const p = project3D(pos.x, pos.y, pos.z, cam);
      if (p.depth < -100) return;
      const sx = w / 2 + p.sx;
      const sy = h / 2 + p.sy;
      const dist = Math.sqrt((mx - sx) ** 2 + (my - sy) ** 2);
      // Hit radius matches dynamic sizing
      const imp = nodeImportance[node.id] || 0.3;
      const hitRadius = Math.max(8, (5 + imp * 23 + 4) * p.scale);
      if (dist < hitRadius && dist < closestDist) {
        closestDist = dist;
        closest = node;
      }
    });
    return closest;
  }, [filteredData, positions, activePositions, focusedNode, nodeImportance]);

  const dragDistance = useRef(0);
  const handleMouseDown = (e) => {
    dragDistance.current = 0;
    if (e.button === 1 || (e.button === 0 && e.shiftKey)) {
      // Middle-click or Shift+click = pan
      isPanning.current = true;
    } else {
      isDragging.current = true;
    }
    lastMouse.current = { x: e.clientX, y: e.clientY };
    setContextMenu(null);
  };

  const handleMouseMove = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    if (isPanning.current) {
      const dx = e.clientX - lastMouse.current.x;
      const dy = e.clientY - lastMouse.current.y;
      dragDistance.current += Math.abs(dx) + Math.abs(dy);
      cameraRef.current.targetX -= dx * 1.5;
      cameraRef.current.targetY -= dy * 1.5;
      lastMouse.current = { x: e.clientX, y: e.clientY };
    } else if (isDragging.current) {
      const dx = e.clientX - lastMouse.current.x;
      const dy = e.clientY - lastMouse.current.y;
      dragDistance.current += Math.abs(dx) + Math.abs(dy);
      cameraRef.current.targetRotY += dx * 0.005;
      cameraRef.current.targetRotX += dy * 0.005;
      cameraRef.current.targetRotX = Math.max(-1.2, Math.min(1.2, cameraRef.current.targetRotX));
      lastMouse.current = { x: e.clientX, y: e.clientY };
    } else {
      const node = getNodeAtPosition(mx, my);
      setHoveredNode(node?.id || null);
      if (canvasRef.current) {
        canvasRef.current.style.cursor = node ? 'pointer' : (e.shiftKey ? 'move' : 'grab');
      }
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    isPanning.current = false;
  };

  const clickTimerRef = useRef(null);
  const handleClick = (e) => {
    // Suppress click if user was dragging/orbiting
    if (dragDistance.current > 5) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const node = getNodeAtPosition(mx, my);
    if (node) {
      // Single click: select + drill into node for detail exploration
      setSelectedNode(node.id);
      setBlastRadiusNodes(new Set());
      // Use a short delay to allow double-click to override
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      clickTimerRef.current = setTimeout(() => {
        drillInto(node);
      }, 250);
    } else {
      setSelectedNode(null);
      setBlastRadiusNodes(new Set());
    }
  };

  const handleDoubleClick = async (e) => {
    // Cancel the single-click drill timer (double-click drills immediately)
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const node = getNodeAtPosition(mx, my);
    if (node) {
      // Drill INTO the node instead of navigating away
      drillInto(node);
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      zoomIn();
    } else {
      zoomOut();
    }
  };

  const handleContextMenu = async (e) => {
    e.preventDefault();
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const node = getNodeAtPosition(mx, my);
    if (node) {
      setContextMenu({ x: e.clientX, y: e.clientY, node });
    }
  };

  // ─── Context Menu Actions ───────────────────────────────────
  const showBlastRadius = async (nodeId) => {
    setContextMenu(null);
    const res = await intelligenceGraphBlastRadius(nodeId);
    if (res.ok) {
      const ids = new Set(res.data.affectedNodes.map(n => n.id));
      ids.add(nodeId);
      setBlastRadiusNodes(ids);
    }
  };

  const showDependencies = async (nodeId) => {
    setContextMenu(null);
    const res = await intelligenceGraphDependencies(nodeId);
    if (res.ok) {
      const ids = new Set(res.data.dependencies.map(n => n.id));
      ids.add(nodeId);
      setBlastRadiusNodes(ids);
    }
  };

  // ─── Search ─────────────────────────────────────────────────
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) { setSearchResults([]); return; }
    const res = await intelligenceGraphSearch(query);
    if (res.ok) setSearchResults(res.data);
  };

  const flyToNode = (node) => {
    const posMap = focusedNode ? activePositions : positions;
    const pos = posMap[node.id];
    if (pos) {
      cameraRef.current.targetX = pos.x;
      cameraRef.current.targetY = pos.y;
      cameraRef.current.targetZ = pos.z + 200;
    }
    setSelectedNode(node.id);
    setShowSearch(false);
    setSearchQuery('');
  };

  // ─── Legend ─────────────────────────────────────────────────
  const legend = useMemo(() => {
    const types = {};
    filteredData.nodes.forEach(n => { types[n.type] = (types[n.type] || 0) + 1; });
    return Object.entries(types).sort((a, b) => b[1] - a[1]);
  }, [filteredData]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: embedded ? 300 : '100%', background: BG, borderRadius: 12 }}>
        <div style={{ textAlign: 'center', color: TEXT_SECONDARY }}>
          <div style={{ fontSize: 20, marginBottom: 8 }}>Loading Knowledge Graph...</div>
          <div style={{ fontSize: 13, color: TEXT_MUTED }}>Constructing platform topology</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: embedded ? 300 : '100%', background: BG, borderRadius: 12 }}>
        <div style={{ color: '#EF4444', textAlign: 'center' }}>
          <div style={{ fontSize: 16, marginBottom: 8 }}>{error}</div>
          <button onClick={loadGraph} style={{ background: BRAND.primary, color: '#fff',
            border: 'none', padding: '6px 16px', borderRadius: 6, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const containerHeight = embedded ? 300 : 'calc(100vh - 160px)';

  // Shared button style for control panels
  const ctrlBtn = (active) => ({
    background: active ? BRAND.primary : BUTTON_BG,
    border: active ? 'none' : `1px solid ${PANEL_BORDER}`,
    color: active ? '#fff' : BUTTON_TEXT,
    padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
    backdropFilter: 'blur(8px)', fontWeight: 600, lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 28, minHeight: 28,
  });

  return (
    <div style={{ position: 'relative', height: containerHeight, background: BG,
      borderRadius: 12, overflow: 'hidden', transition: 'background 0.3s' }}>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        onWheel={handleWheel}
        onContextMenu={handleContextMenu}
        style={{ width: '100%', height: '100%', cursor: 'grab' }}
      />

      {/* ─── Top Bar: Breadcrumbs + Top Controls ─── */}
      <div style={{ position: 'absolute', top: 12, left: 12, right: 12,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        pointerEvents: 'none' }}>

        {/* Breadcrumbs with drill-up */}
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', pointerEvents: 'auto', flexWrap: 'wrap' }}>
          {breadcrumbs.map((bc, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span style={{ color: TEXT_MUTED, fontSize: 12 }}>/</span>}
              <button onClick={() => drillToBreadcrumb(i)}
                style={{
                  background: i === breadcrumbs.length - 1 ? (BRAND.primary + '30') : BUTTON_BG,
                  border: `1px solid ${i === breadcrumbs.length - 1 ? BRAND.primary + '60' : PANEL_BORDER}`,
                  color: i === breadcrumbs.length - 1 ? BRAND.primary : BUTTON_TEXT,
                  padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                  backdropFilter: 'blur(8px)', fontWeight: i === breadcrumbs.length - 1 ? 700 : 400,
                }}>
                {bc.label}
              </button>
            </React.Fragment>
          ))}
          {breadcrumbs.length > 1 && (
            <button onClick={drillUp} style={{
              ...ctrlBtn(false), padding: '4px 8px', fontSize: 14, marginLeft: 4,
            }} title="Drill Up (Esc)">↑</button>
          )}
        </div>

        {/* Top-right controls */}
        <div style={{ display: 'flex', gap: 6, pointerEvents: 'auto' }}>
          <button onClick={() => setShowControls(s => !s)} style={ctrlBtn(showControls)} title="Toggle Controls (C)">
            Controls
          </button>
          <button onClick={() => setShowSearch(!showSearch)} style={ctrlBtn(showSearch)} title="Search (F)">
            Search
          </button>
          <button onClick={loadGraph} style={ctrlBtn(false)} title="Refresh data">
            Refresh
          </button>
        </div>
      </div>

      {/* ─── Right-side Navigation Controls ─── */}
      {showControls && (
        <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
          display: 'flex', flexDirection: 'column', gap: 4, pointerEvents: 'auto' }}>

          {/* Zoom controls */}
          <button onClick={zoomIn} style={ctrlBtn(false)} title="Zoom In (+)">+</button>
          <div style={{
            background: BUTTON_BG, border: `1px solid ${PANEL_BORDER}`, borderRadius: 6,
            padding: '3px 6px', fontSize: 10, fontWeight: 700, color: TEXT_MUTED,
            textAlign: 'center', backdropFilter: 'blur(8px)',
          }}>{zoomPercent}%</div>
          <button onClick={zoomOut} style={ctrlBtn(false)} title="Zoom Out (-)">−</button>

          <div style={{ height: 4 }} />

          {/* Pan controls */}
          <button onClick={() => panCamera(0, 40, 0)} style={ctrlBtn(false)} title="Pan Up (↑)">↑</button>
          <div style={{ display: 'flex', gap: 4 }}>
            <button onClick={() => panCamera(40, 0, 0)} style={ctrlBtn(false)} title="Pan Left (←)">←</button>
            <button onClick={() => panCamera(-40, 0, 0)} style={ctrlBtn(false)} title="Pan Right (→)">→</button>
          </div>
          <button onClick={() => panCamera(0, -40, 0)} style={ctrlBtn(false)} title="Pan Down (↓)">↓</button>

          <div style={{ height: 4 }} />

          {/* Rotation */}
          <button onClick={() => { cameraRef.current.targetRotY -= 0.3; }} style={ctrlBtn(false)} title="Rotate Left">↶</button>
          <button onClick={() => { cameraRef.current.targetRotY += 0.3; }} style={ctrlBtn(false)} title="Rotate Right">↷</button>

          <div style={{ height: 4 }} />

          {/* Center / Fit to screen */}
          <button onClick={centerGraph} style={ctrlBtn(false)} title="Center Graph (G)">⊙</button>

          {/* Home / Reset */}
          <button onClick={resetCamera} style={ctrlBtn(false)} title="Reset View (0)">⌂</button>

          {/* Background toggle */}
          <button onClick={() => setDarkMode(m => !m)} style={ctrlBtn(false)} title="Toggle Background (B)">
            {darkMode ? '☀' : '☾'}
          </button>

          {/* Legend toggle */}
          <button onClick={() => setShowLegend(s => !s)} style={ctrlBtn(showLegend)} title="Toggle Legend (L)">
            ◧
          </button>
        </div>
      )}

      {/* Embedded mode: minimal controls */}
      {embedded && (
        <div style={{ position: 'absolute', right: 8, bottom: 8, display: 'flex', gap: 4, pointerEvents: 'auto' }}>
          <button onClick={zoomIn} style={{ ...ctrlBtn(false), padding: '2px 6px', fontSize: 11 }}>+</button>
          <button onClick={zoomOut} style={{ ...ctrlBtn(false), padding: '2px 6px', fontSize: 11 }}>−</button>
          <button onClick={() => setDarkMode(m => !m)} style={{ ...ctrlBtn(false), padding: '2px 6px', fontSize: 11 }}>
            {darkMode ? '☀' : '☾'}
          </button>
        </div>
      )}

      {/* Search Overlay */}
      {showSearch && (
        <div style={{ position: 'absolute', top: 48, right: 12, width: 280,
          background: PANEL_BG, borderRadius: 8, padding: 12,
          backdropFilter: 'blur(12px)', border: `1px solid ${PANEL_BORDER}` }}>
          <input
            type="text" value={searchQuery} onChange={e => handleSearch(e.target.value)}
            placeholder="Search entities..."
            autoFocus
            style={{ width: '100%', background: darkMode ? STONE[900] : STONE[50],
              border: `1px solid ${PANEL_BORDER}`,
              borderRadius: 6, padding: '6px 10px', color: TEXT_PRIMARY, fontSize: 13,
              outline: 'none', boxSizing: 'border-box' }}
          />
          {searchResults.length > 0 && (
            <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto' }}>
              {searchResults.slice(0, 10).map(node => (
                <button key={node.id} onClick={() => flyToNode(node)}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    background: 'none', border: 'none', color: TEXT_PRIMARY, padding: '6px 8px',
                    borderRadius: 4, cursor: 'pointer', textAlign: 'left', fontSize: 12 }}
                  onMouseEnter={e => e.target.style.background = HOVER_BG}
                  onMouseLeave={e => e.target.style.background = 'none'}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%',
                    background: NODE_COLORS[node.type] || '#6B7280', flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{node.name}</span>
                  <span style={{ color: TEXT_MUTED, fontSize: 10 }}>{node.type}</span>
                </button>
              ))}
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 10, color: TEXT_MUTED }}>
            Click a node to drill in · Esc to drill out
          </div>
        </div>
      )}

      {/* Stats Bar */}
      <div style={{ position: 'absolute', bottom: 12, left: 12,
        display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ background: BUTTON_BG, borderRadius: 6, padding: '4px 10px',
          backdropFilter: 'blur(8px)', fontSize: 11, color: TEXT_SECONDARY,
          border: `1px solid ${PANEL_BORDER}` }}>
          {filteredData.nodes.length} nodes · {filteredData.edges.length} edges
          {focusedNode && <span> · focused on {focusedNode.name}</span>}
        </div>
        {blastRadiusNodes.size > 0 && (
          <div style={{ background: '#EF4444' + '20', borderRadius: 6, padding: '4px 10px',
            backdropFilter: 'blur(8px)', fontSize: 11, color: '#EF4444', fontWeight: 600,
            border: '1px solid #EF444440' }}>
            Blast radius: {blastRadiusNodes.size} affected
          </div>
        )}
      </div>

      {/* Legend */}
      {showLegend && !embedded && (
        <div style={{ position: 'absolute', bottom: 12, right: 54,
          background: PANEL_BG, borderRadius: 8, padding: 10,
          backdropFilter: 'blur(8px)', maxWidth: 180, border: `1px solid ${PANEL_BORDER}` }}>
          <div style={{ fontSize: 10, color: TEXT_MUTED, marginBottom: 6, fontWeight: 600 }}>ENTITY TYPES</div>
          {legend.slice(0, 8).map(([type, count]) => (
            <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 10, color: TEXT_SECONDARY, marginBottom: 3, cursor: 'pointer' }}
              onClick={() => {
                // Click legend item to filter to that type + its connections
                setVisibleTypes(prev => {
                  if (prev && prev.has(type) && prev.size === 1) return null; // Toggle off
                  return new Set([type]);
                });
              }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%',
                background: NODE_COLORS[type] || '#6B7280', flexShrink: 0 }} />
              <span style={{ flex: 1 }}>{type}</span>
              <span style={{ color: TEXT_MUTED }}>{count}</span>
            </div>
          ))}
          {visibleTypes && (
            <button onClick={() => setVisibleTypes(null)}
              style={{ marginTop: 6, background: 'none', border: 'none', color: BRAND.primary,
                fontSize: 10, cursor: 'pointer', padding: 0, fontWeight: 600 }}>
              Show All
            </button>
          )}
          <div style={{ marginTop: 8, borderTop: `1px solid ${PANEL_BORDER}`, paddingTop: 6 }}>
            <div style={{ fontSize: 9, color: TEXT_MUTED, marginBottom: 4, fontWeight: 600 }}>HEALTH</div>
            {Object.entries(HEALTH_COLORS).slice(0, 4).map(([band, color]) => (
              <div key={band} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 9, color: TEXT_SECONDARY, marginBottom: 2 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
                <span>{band}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div style={{ position: 'fixed', left: contextMenu.x, top: contextMenu.y,
          background: darkMode ? STONE[700] : '#fff', borderRadius: 8, padding: 4, minWidth: 200,
          border: `1px solid ${PANEL_BORDER}`, boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          zIndex: 1000 }}>
          <div style={{ padding: '6px 12px', fontSize: 12, color: TEXT_SECONDARY,
            borderBottom: `1px solid ${PANEL_BORDER}`, fontWeight: 600 }}>
            {contextMenu.node.name}
          </div>
          {[
            { label: '🔍 Drill Into', action: () => { drillInto(contextMenu.node); setContextMenu(null); }},
            { label: '💥 Show Blast Radius', action: () => showBlastRadius(contextMenu.node.id) },
            { label: '🔗 Show Dependencies', action: () => showDependencies(contextMenu.node.id) },
            { label: '📋 View Details', action: async () => {
              const res = await intelligenceGraphNode(contextMenu.node.id);
              if (res.ok) setNodeDetail(res.data);
              setContextMenu(null);
            }},
            { label: '✈ Fly To', action: () => { flyToNode(contextMenu.node); setContextMenu(null); }},
          ].map(item => (
            <button key={item.label} onClick={item.action}
              style={{ display: 'block', width: '100%', background: 'none', border: 'none',
                color: TEXT_PRIMARY, padding: '6px 12px', textAlign: 'left', cursor: 'pointer',
                fontSize: 12, borderRadius: 4 }}
              onMouseEnter={e => e.target.style.background = HOVER_BG}
              onMouseLeave={e => e.target.style.background = 'none'}>
              {item.label}
            </button>
          ))}
        </div>
      )}

      {/* Node Detail Panel */}
      {nodeDetail && (
        <div style={{ position: 'absolute', top: 48, left: 12, width: 300,
          background: PANEL_BG, borderRadius: 8, padding: 16,
          backdropFilter: 'blur(12px)', border: `1px solid ${PANEL_BORDER}`,
          maxHeight: 'calc(100% - 80px)', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY }}>{nodeDetail.name}</div>
            <button onClick={() => setNodeDetail(null)}
              style={{ background: 'none', border: 'none', color: TEXT_MUTED, cursor: 'pointer',
                fontSize: 16 }}>
              ✕
            </button>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <span style={{ background: NODE_COLORS[nodeDetail.type] + '20',
              color: NODE_COLORS[nodeDetail.type], padding: '2px 8px', borderRadius: 4,
              fontSize: 11, fontWeight: 600 }}>{nodeDetail.type}</span>
            <span style={{ background: HEALTH_COLORS[nodeDetail.healthBand] + '20',
              color: HEALTH_COLORS[nodeDetail.healthBand], padding: '2px 8px', borderRadius: 4,
              fontSize: 11, fontWeight: 600 }}>{Math.round(nodeDetail.health)}% {nodeDetail.healthBand}</span>
          </div>

          {/* Drill-in button */}
          <button onClick={() => {
            const node = (graphData.nodes || []).find(n => n.id === nodeDetail.id);
            if (node) drillInto(node);
          }} style={{
            marginTop: 10, width: '100%', padding: '6px 12px', borderRadius: 6,
            background: BRAND.primary + '15', border: `1px solid ${BRAND.primary}40`,
            color: BRAND.primary, fontSize: 12, fontWeight: 600, cursor: 'pointer',
          }}>
            Drill Into Structure →
          </button>

          {nodeDetail.blastRadius > 0 && (
            <div style={{ marginTop: 10, fontSize: 12, color: TEXT_SECONDARY }}>
              Blast Radius: <span style={{ color: '#EF4444', fontWeight: 600 }}>
                {nodeDetail.blastRadius} nodes
              </span>
            </div>
          )}

          {nodeDetail.outboundEdges?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: TEXT_MUTED, fontWeight: 600, marginBottom: 4 }}>
                OUTBOUND ({nodeDetail.outboundEdges.length})
              </div>
              {nodeDetail.outboundEdges.slice(0, 8).map(e => (
                <div key={e.id} style={{ fontSize: 11, color: TEXT_SECONDARY, marginBottom: 2 }}>
                  {e.type} → {String(e.target).slice(0, 8)}...
                </div>
              ))}
            </div>
          )}

          {nodeDetail.inboundEdges?.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: TEXT_MUTED, fontWeight: 600, marginBottom: 4 }}>
                INBOUND ({nodeDetail.inboundEdges.length})
              </div>
              {nodeDetail.inboundEdges.slice(0, 8).map(e => (
                <div key={e.id} style={{ fontSize: 11, color: TEXT_SECONDARY, marginBottom: 2 }}>
                  {e.type} ← {String(e.source).slice(0, 8)}...
                </div>
              ))}
            </div>
          )}

          {nodeDetail.attributes && Object.keys(nodeDetail.attributes).length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: TEXT_MUTED, fontWeight: 600, marginBottom: 4 }}>
                ATTRIBUTES
              </div>
              {Object.entries(nodeDetail.attributes).map(([k, v]) => (
                <div key={k} style={{ fontSize: 11, color: TEXT_SECONDARY, marginBottom: 2 }}>
                  <span style={{ color: TEXT_MUTED }}>{k}:</span> {String(v)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Keyboard Shortcuts Help */}
      {!embedded && (
        <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
          background: BUTTON_BG, borderRadius: 6, padding: '3px 10px',
          backdropFilter: 'blur(8px)', fontSize: 10, color: TEXT_MUTED,
          border: `1px solid ${PANEL_BORDER}`, whiteSpace: 'nowrap' }}>
          Drag: orbit · Shift+drag: pan · Scroll: zoom · Click: drill in · Esc: drill out · G: center · C: controls · B: bg
        </div>
      )}
    </div>
  );
}
